// R12-1 — THE THREE FIELD BLOCKS EVERY BIRTH FORM ON THIS SITE IS BUILT FROM.
//
// Twelfth, thirteenth and fourteenth components out of App.tsx, and they move for the same reason
// the tarot views did in R11: the thing that USES them has to be reachable by a gate, and a
// component cannot import back out of App.tsx. `CreateRoomForm` — the front door, the form every
// member fills in to exist here — holds no hook and is therefore callable, but only once these are
// somewhere it can import them from.
//
// All three hold state, which is R8's line. R10-3 settled what that line actually is and R11-1
// applied it again: **the question is whether the extraction LOSES REACH, not whether the file holds
// a hook.** All three were already beyond the loader inside App.tsx, and the stubbed JSX runtime
// never CALLS a child component — it turns `<NameFields .../>` into a plain `{ type, props }` — so a
// hookless caller that renders them is fully callable out here and was not in there. Reach is gained.
//
// `BirthChartForm` comes too, because it is the shape these fields patch and the shape the form
// takes; App.tsx imports it back, where sixty-odd flows are typed by it. Moved VERBATIM.

import { useEffect, useId, useRef, useState } from "react";
import { COUNTRY_NAMES, COUNTRY_NAMES_BY_CODE, countryCodeOf } from "../countryCodes.js";
import { withNameParts } from "../nameParts.js";
// 14-B2: every visible word in these three blocks now DEFAULTS from the strings catalog, resolved
// against the `language` the caller hands down. It is a default and never an override, because this
// copy also arrives as props at a dozen call sites (R24) — a block that translated over its caller
// would silently rename fields the caller deliberately worded. No language passed is English, so
// every existing call site is byte-identical.
import { DEFAULT_LANGUAGE } from "../language.js";
import { t } from "../strings.js";
// R97: the one place this house asks where a place is, and the one place that decides what a
// non-answer means. Both readers of that route sit on it, so "we could not look" cannot be true of
// one field and false of the other.
import { keepsResults, lookupStateFor, PLACE_MIN_QUERY, placePickNote, searchPlaces, type PlaceLookupState } from "../lib/place-search";

export type BirthChartForm = {
  // I6 R1 (owner, 2026-07-16): WHICH ROW this chart came from — the member's own card
  // ("profile") or one specific person of interest ("person" + that row's id). Stamped by the
  // ONE shared SubjectPicker and posted with the chart, so the server keys the canonical stored
  // chart to the exact row the member selected instead of matching on a name. Absent = "someone
  // new", which owns no row and so has no canonical chart.
  subjectKind?: "profile" | "person";
  subjectId?: string;
  name: string;
  firstName: string;
  middleName: string;
  lastName: string;
  noMiddleName?: boolean;
  birthDate: string;
  birthTime: string;
  birthPeriod: "AM" | "PM";
  birthTimeUnknown: boolean;
  birthplace: string;
  birthRegion: string;
  birthCountry: string;
  latitude: string;
  longitude: string;
  timezone: string;
  houseType: string;
  intention: string;
  // N19-1: WHERE SHE LIVES NOW, which is a different fact from where she was born and is kept a
  // different fact all the way down. The natal chart never reads these; transits, daily guidance
  // and the current horoscopes read nothing else.
  currentPlace?: string;
  currentRegion?: string;
  currentCountry?: string;
  currentLatitude?: string;
  currentLongitude?: string;
  currentTimezone?: string;
  currentTimezoneName?: string;
  // Server-computed, never client-computed: has she been asked once where she lives now? The law
  // that decides lives in server/location-law.js and the room only renders the answer.
  needsCurrentLocation?: boolean;
  currentLocationPrompted?: boolean;
};

// ITEM 5 paid its App.tsx lines the way this roadmap taught (R37/R42): MOVE THE DATA. The EMPTY form
// belongs beside the type it is an instance of — it was 18 lines of pure data in the monolith, 350
// lines from that type, and nine call sites spread it. Nothing read it as source text (measured
// before the move, R9's lesson).
export const initialBirthChart: BirthChartForm = {
  name: "",
  firstName: "",
  middleName: "",
  lastName: "",
  birthDate: "",
  birthTime: "",
  birthPeriod: "AM",
  birthTimeUnknown: false,
  birthplace: "",
  birthRegion: "",
  birthCountry: "",
  latitude: "",
  longitude: "",
  timezone: "",
  houseType: "placidus",
  intention: "",
};


// A live result from the AstrologyAPI geo_details lookup (real geocoding).
export type BirthplaceResult = {
  place_name: string;
  state?: string;
  country?: string;
  country_code: string;
  latitude: string;
  longitude: string;
  timezone_id: string;
  // AC2: the anywhere scope also returns the full formatted label and the vendor result type,
  // so same-named places are distinguishable in the list.
  formatted?: string;
  result_type?: string;
};

// "City, State/Region, Country" — full birthplace so the report is verifiable.
// Region/country come from Geoapify; falls back to the code map when absent.
export function birthplaceResultLabel(result: BirthplaceResult) {
  const country = result.country || COUNTRY_NAMES_BY_CODE[result.country_code] || result.country_code;
  return [result.place_name, result.state, country].filter(Boolean).join(", ");
}

// AC2 / R118 — **A RESOLVED PLACE TRAVELS WHOLE, AND SO DOES AN UNRESOLVED ONE.**
//
// AC2 is written in the astrocartography room ("the resolved place carries its full identity
// through — coordinates AND zone") and that room obeys it unconditionally: it sends
// `timezone: place.timezone || ""`, always. **THIS COMPONENT — the one every birth form and the
// current-location step on this site is built from — did not.** The IANA name was emitted as a
// CONDITIONAL spread (`...(match.timezone_id ? { timezoneName } : {})`) and the free-text branch,
// which clears the place, the region, the coordinates and the offset, never mentioned it at all.
//
// **A PATCH IS MERGED, SO AN OMITTED KEY IS NOT "NO ZONE" — IT IS THE LAST PLACE'S ZONE.** Every
// receiver reads `if (patch.x !== undefined)`, so picking a place whose geocoding answer carries no
// `timezone_id` moved her coordinates and her offset and left the PREVIOUS city's IANA name sitting
// on the row. **A stale zone name is worse than none**, and that is arithmetic rather than an
// opinion: `localDayFor` prefers the IANA name over the numeric offset precisely because the name
// is the half that survives daylight saving — so the stale name OUTRANKS the fresh offset that is
// correct for the new place, and her fair-use day turns over on a clock she does not live on.
//
// **THE COMPONENT ALREADY KNEW THIS RULE AND ALREADY ENFORCED IT — FOR THE OFFSET, TWENTY LINES
// UP.** The timezone effect's own comment reads *"the place or the birth date has MOVED under it:
// it was resolved for somewhere/somewhen else and is now stale"*, and it re-resolves. Two fields
// travel with a place; one of them had the guard.
//
// Both patches are PURE and EXPORTED so a gate can call them — `BirthPlaceFields` holds four hooks,
// so until they came out here the only instrument that could reach this decision was a browser
// driving a live geocoder. **The law `scripts/r118-place-zone.mjs` runs is a BICONDITIONAL over the
// UNION OF THE TWO PATCHES' OWN KEYS, not over a hand-typed list of fields: a key that one patch
// answers and the other does not is RED unless it is declared below with its reason.** That is what
// makes the next fact to travel with a place held by existing rather than by somebody remembering —
// and it is exactly the check that would have caught this one, because `timezoneName` was in the
// resolved patch and in neither the free-text branch nor any roster.
export const PLACE_PATCH_ASYMMETRIES = {
  birthCountry: "SET by a resolved place (and only when the country select is still empty), never CLEARED: typing in the CITY box must not wipe the country she already chose.",
  timezone: "CLEARED by free text, never SET by a resolved place: the numeric offset is not on a geocoding result. It is a fact about a place ON A DAY, and the picker does not know the day, so the effect below resolves it from coordinates plus the birth date.",
};
export function resolvedPlacePatch(match: BirthplaceResult, currentCountry = "") {
  const resolvedCountry = match.country || COUNTRY_NAMES_BY_CODE[match.country_code] || match.country_code;
  return {
    // Store the clean city + region separately so the report reads "City, Region, Country" — but
    // keep the full label visible in the field.
    birthplace: match.place_name,
    birthRegion: match.state || "",
    latitude: match.latitude,
    longitude: match.longitude,
    // N19-1 REMAINDER: the IANA name rides along with the place it belongs to. **UNCONDITIONALLY**
    // — empty when the fallback geocoder did not supply one, which is exactly when the offset is
    // all we have, and exactly when a leftover name from the last place would win over it.
    timezoneName: match.timezone_id || "",
    ...(resolvedCountry && !currentCountry.trim() ? { birthCountry: resolvedCountry } : {}),
  };
}

export function clearedPlacePatch(value: string) {
  return { birthplace: value, birthRegion: "", latitude: "", longitude: "", timezone: "", timezoneName: "" };
}

// THE single First / Middle / Last name input group, used by EVERY form that feeds a
// name into numerology (birth chart, create-room, Find Your Type, numerology, People of
// Interest). The middle name is its own field, never labeled "optional" — the full name
// at birth (middle included) is what makes numerology accurate; the accompanying hint
// tells people why. If you add a name form, use THIS component — do not hand-roll inputs.
// Shared name inputs. When requireMiddle is set (any form that feeds numerology), the middle
// name is HARD REQUIRED site-wide: the field goes red with "Your middle name is required for
// numerology" and the native `required` attribute blocks submit, so no numerology ever runs on
// a partial name. The only legitimate bypass is the "I don't have a middle name" checkbox.
// (NameFields lesson: enforce once here, it propagates to every caller.)
// OV2-1(a): EVERY name this site collects is a name AT BIRTH, so the note lives in the shared field
// rather than in the wording of a dozen forms that would drift apart. A married name silently
// entered here is not a small error: the numerology is built from those exact letters.
// DERIVED from the catalog rather than typed a second time here (R13): the sentence a member reads is
// the row the gate holds in both languages. The constant stays exported because it is the English
// answer and callers ask for it by name.
export const NAME_AT_BIRTH_NOTE = t(DEFAULT_LANGUAGE, "name.atBirthNote");

export function NameFields({ first, middle, last, onChange, requireMiddle = false, noMiddle = false, onNoMiddle, note, language = DEFAULT_LANGUAGE }: {
  first: string;
  middle: string;
  last: string;
  onChange: (field: "firstName" | "middleName" | "lastName", value: string) => void;
  requireMiddle?: boolean;
  noMiddle?: boolean;
  onNoMiddle?: (checked: boolean) => void;
  note?: string;
  language?: string;
}) {
  const [touched, setTouched] = useState(false);
  const middleMissing = requireMiddle && !noMiddle && !middle.trim();
  const showMiddleError = touched && middleMissing;
  const birthNote = note === undefined ? t(language, "name.atBirthNote") : note;
  return (
    <div className="name-fields">
      <label>
        <span>{t(language, "name.first")}</span>
        <input value={first} onChange={(e) => onChange("firstName", e.target.value)} placeholder={t(language, "name.firstPlaceholder")} required />
      </label>
      <label className={showMiddleError ? "name-fields__middle is-error" : "name-fields__middle"}>
        <span>{t(language, "name.middle")}</span>
        <input
          value={middle}
          onChange={(e) => onChange("middleName", e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={t(language, "name.middlePlaceholder")}
          required={requireMiddle && !noMiddle}
          aria-invalid={showMiddleError || undefined}
        />
        {showMiddleError ? <span className="name-fields__error">{t(language, "name.middleRequired")}</span> : null}
        {requireMiddle && onNoMiddle ? (
          <label className="name-fields__nomiddle">
            <input type="checkbox" checked={noMiddle} onChange={(e) => onNoMiddle(e.target.checked)} />
            {t(language, "name.noMiddle")}
          </label>
        ) : null}
      </label>
      <label>
        <span>{t(language, "name.last")}</span>
        <input value={last} onChange={(e) => onChange("lastName", e.target.value)} placeholder={t(language, "name.lastPlaceholder")} required />
      </label>
      {/* Full width UNDER the three boxes, as a fourth grid child, so the N19-2 one-line alignment
          of first / middle / last is untouched by it. */}
      {birthNote ? <p className="name-fields__birthnote">{birthNote}</p> : null}
    </div>
  );
}

// 12-hour birth-time entry: separate Hour (1-12), Minute (00-59) and AM/PM
// selects. Stores birthTime as "H:MM" — no more fragile text parsing that blocked
// minutes. Emits patches with the form's field names.
export function BirthTimeFields({
  birthTime,
  birthPeriod,
  disabled,
  onChange,
  language = DEFAULT_LANGUAGE,
}: {
  birthTime: string;
  birthPeriod: "AM" | "PM";
  disabled?: boolean;
  onChange: (patch: { birthTime?: string; birthPeriod?: "AM" | "PM" }) => void;
  language?: string;
}) {
  const [rawHour, rawMinute] = birthTime.split(":");
  const hour = rawHour || "";
  const minute = rawMinute ?? "";
  return (
    <div className="time-field">
      <select
        className="time-field__select"
        value={hour}
        disabled={disabled}
        required={!disabled}
        aria-label={t(language, "time.hour")}
        onChange={(event) => onChange({ birthTime: `${event.target.value}:${minute || "00"}` })}
      >
        <option value="" disabled>{t(language, "time.hourShort")}</option>
        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="time-field__colon">:</span>
      <select
        className="time-field__select"
        value={minute}
        disabled={disabled}
        required={!disabled}
        aria-label={t(language, "time.minute")}
        onChange={(event) => onChange({ birthTime: `${hour || "12"}:${event.target.value}` })}
      >
        <option value="" disabled>{t(language, "time.minuteShort")}</option>
        {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <select
        value={birthPeriod}
        disabled={disabled}
        aria-label={t(language, "time.period")}
        onChange={(event) => onChange({ birthPeriod: event.target.value as "AM" | "PM" })}
      >
        <option>AM</option>
        <option>PM</option>
      </select>
    </div>
  );
}

// Country + birthplace city with live geo_details autocomplete (filtered by the
// chosen country) and DST-correct timezone resolution. Country is entered first;
// city suggestions appear as you type. Emits patches with the form's field names.
export function BirthPlaceFields({
  country,
  birthplace,
  latitude,
  longitude,
  timezone,
  birthDate,
  onChange,
  // N19-1: the same field now collects WHERE SHE LIVES NOW as well as where she was born. Only the
  // words and the date the timezone is resolved against differ, so only those are props. Defaults
  // are the birth wording, which leaves every existing caller unchanged.
  // OV2-1(a): "Country" said nothing about WHICH country. Every default here names the birth, and
  // the current-location step passes its own words over the top.
  // 14-B2: the defaults are RESOLVED FROM THE CATALOG per language rather than typed here, and a
  // caller's own words still win — `labels` is an override, never a thing this block translates over.
  labels,
  language = DEFAULT_LANGUAGE,
}: {
  country: string;
  birthplace: string;
  latitude: string;
  longitude: string;
  timezone: string;
  // For a BIRTH place this is the birth date. For a CURRENT place it is TODAY, because a current
  // timezone has to be right about today's daylight saving, not about the day she was born.
  birthDate: string;
  labels?: { country: string; city: string; countryPlaceholder: string; cityPlaceholder: string };
  language?: string;
  onChange: (patch: {
    birthCountry?: string;
    birthplace?: string;
    birthRegion?: string;
    latitude?: string;
    longitude?: string;
    timezone?: string;
    // N19-1 REMAINDER: the IANA zone NAME beside the numeric offset. The offset is what the
    // astrology vendor takes, but it is only true on one side of a DST boundary, so "her local
    // midnight" (the fair-use reset) has to be measured against the name. The search result already
    // carries it, so filling this column costs nothing and buys nothing extra from any vendor.
    timezoneName?: string;
  }) => void;
}) {
  const words = {
    country: labels?.country ?? t(language, "place.birthCountry"),
    city: labels?.city ?? t(language, "place.birthCity"),
    countryPlaceholder: labels?.countryPlaceholder ?? t(language, "place.birthCountryPlaceholder"),
    cityPlaceholder: labels?.cityPlaceholder ?? t(language, "place.birthCityPlaceholder"),
  };
  const countryListId = useId();
  const cityListId = useId();
  const [results, setResults] = useState<BirthplaceResult[]>([]);
  // I1: the member must SEE the mechanism. `lookup` drives the inline row under the field and
  // `open` controls the suggestion list. A native <datalist> could do neither (its dropdown is
  // unstyleable browser UI that cannot show a state, and cannot be asserted in a probe), which
  // is why the city now owns a real list. The COUNTRY stays a datalist: country-first is a
  // requirement, not a responsiveness problem.
  //
  // R97: this was a BOOLEAN, and a boolean cannot say "we could not look". Searching-or-not plus an
  // empty list drew "No matches yet. Keep typing." over a lookup that never answered — a sentence
  // about her town, and an instruction to go on doing the thing that is not working, on the one
  // field with no coordinates behind it and therefore no reading beyond it.
  const [lookup, setLookup] = useState<PlaceLookupState>("idle");
  const searching = lookup === "searching";
  const [open, setOpen] = useState(false);
  // I2: is the text in this field the member's QUERY, or a location we already hold? A picker fill
  // (For me / a circle person) and a committed suggestion are both fully-resolved locations that
  // the DB already knows. Searching for them again re-buys geodata we own (cost law) and is what
  // made picking a saved person re-request their city from Geoapify. Only real typing is a query.
  const [fromFill, setFromFill] = useState(true);
  // The visible field text is decoupled from the stored city: after a selection
  // the field shows the FULL "City, Region, Country" the user picked, while the
  // form stores the clean city + region + country + coordinates separately.
  const [display, setDisplay] = useState(birthplace);
  const skipSync = useRef(false);
  const boxRef = useRef<HTMLLabelElement | null>(null);
  // I1: the newest keystroke owns the field. Without this, a slow reply for "Den" could land
  // AFTER a fast reply for "Denver" and overwrite fresh results with stale ones.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (skipSync.current) {
      skipSync.current = false;
      return;
    }
    setDisplay(birthplace);
    // The value arrived from OUTSIDE this field (a picker fill, a profile seed, a person edit).
    // It is a location on record, not something the member is searching for.
    setFromFill(true);
    setResults([]);
    setLookup("idle");
    setOpen(false);
  }, [birthplace]);

  // Debounced place search, scoped to the chosen country. I1: first request at TWO characters
  // (was three) and a ~200ms debounce (was 350ms), so suggestions arrive while the member is
  // still typing instead of after they have given up.
  useEffect(() => {
    // I2: never search for a location we already hold. A filled field is the member's stored
    // row, resolved when they last saved it; the whole point of the fill is that they do not
    // have to look it up again.
    if (fromFill) return;
    const query = display.trim();
    if (query.length < PLACE_MIN_QUERY) {
      abortRef.current?.abort();
      setResults([]);
      setLookup("idle");
      return;
    }
    let active = true;
    // Shown the MOMENT typing starts, not when the request leaves: the debounce is exactly the
    // window in which the member decides nothing is happening.
    setLookup("searching");
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      // R97: Geoapify handles the lookup; pass the chosen country's code so results are biased to
      // it (filtered client-side below too). The ANSWER decides what this field draws — including
      // I1's abort ruling, which `lookupStateFor`/`keepsResults` now hold rather than this call
      // site: a newer keystroke winning leaves the list and the row exactly as they were.
      const answer = await searchPlaces<BirthplaceResult>(query, {
        countryCode: countryCodeOf(country),
        signal: controller.signal,
      });
      if (!active) return;
      if (!keepsResults(answer)) setResults(answer.results);
      setLookup((prev) => lookupStateFor(answer, prev));
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [display, country, fromFill]);

  // Close the suggestions on an outside click, matching the circle dropdown's behaviour.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // I2: which place+date does the timezone we are holding actually belong to? A stored row arrives
  // WITH its timezone (resolved when the member last saved it), so re-resolving it buys data we
  // already own. But a timezone is only correct for the coordinates and DATE it was resolved for,
  // so it must be recomputed the moment either moves. This ref is how the field tells those apart.
  const tzSeenRef = useRef<{ tz: string; sig: string }>({ tz: "", sig: "" });

  // Resolve the DST-correct timezone whenever the located place or birth date changes.
  useEffect(() => {
    if (!latitude || !longitude) return;
    const sig = `${latitude}|${longitude}|${birthDate}`;
    const seen = tzSeenRef.current;
    if (timezone) {
      // Either the timezone arrived in the same beat as this place+date (a picker fill, or our own
      // resolve below), or we already resolved it for exactly this place+date. It is correct: keep
      // it and pay nothing.
      if (timezone !== seen.tz || seen.sig === sig) {
        tzSeenRef.current = { tz: timezone, sig };
        return;
      }
      // Same timezone value, but the place or the birth date has MOVED under it: it was resolved
      // for somewhere/somewhen else and is now stale. Fall through and resolve it again.
    }
    let active = true;
    (async () => {
      try {
        const response = await fetch("/api/astrology/timezone", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ latitude, longitude, date: birthDate }),
        });
        const data = await response.json();
        if (active && data.timezone !== undefined && data.timezone !== null) {
          // Record WHAT this answer was resolved for, so the same question is never asked twice.
          tzSeenRef.current = { tz: String(data.timezone), sig };
          onChange({ timezone: String(data.timezone) });
        }
      } catch {
        /* leave timezone as-is */
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, birthDate, timezone]);

  // City suggestions filtered to the chosen country (when we know its code).
  const countryCode = countryCodeOf(country);
  const cityResults = countryCode ? results.filter((result) => result.country_code === countryCode) : results;

  const handleCity = (value: string) => {
    setDisplay(value);
    const match = cityResults.find((result) => birthplaceResultLabel(result) === value);
    if (match) {
      skipSync.current = true;
      onChange(resolvedPlacePatch(match, country));
      // I2: the field now holds a RESOLVED location, not a query. Without this the committed
      // label ("Denver, Colorado, United States") went straight back out as a fresh search for
      // the city the member had just finished picking.
      setFromFill(true);
      setResults([]);
      setLookup("idle");
      setDisplay(birthplaceResultLabel(match));
    } else {
      // Free text: a query, and nothing is resolved behind it any more.
      setFromFill(false);
      skipSync.current = true;
      onChange(clearedPlacePatch(value));
    }
  };

  // I2: a row with city TEXT and no geodata behind it is a dead end — the engine refuses a chart
  // with no coordinates (`buildAstrologyPayload` returns null rather than guessing), so the member
  // hits it much later with nothing to tell them why. Outline the field and say what to do: one pick
  // from the list resolves it for good.
  //
  // >>> **IT #182: THIS ASKED `fromFill` AND THEREFORE ONLY EVER FIRED FOR A LEGACY ROW.** <<< The
  // member typing free text that matches nothing is the case that PRODUCES the dead end, and she was
  // the one case it could not reach. The decision is now `placePickNote`, a pure law a gate RUNS,
  // and it is drawn BELOW the field so it survives the dropdown closing.
  const pickNote = placePickNote({ text: display, hasCoordinates: Boolean(latitude && longitude), lookup });
  const needsConfirm = Boolean(pickNote);

  return (
    <>
      <label>
        <span>{words.country}</span>
        <input
          list={countryListId}
          value={country}
          onChange={(event) => onChange({ birthCountry: event.target.value })}
          placeholder={words.countryPlaceholder}
          required
        />
        <datalist id={countryListId}>
          {COUNTRY_NAMES.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </label>
      <label className="birthplace-field" ref={boxRef}>
        <span>{words.city}</span>
        <input
          className={`birthplace-field__input${needsConfirm ? " birthplace-field__input--confirm" : ""}`}
          value={display}
          onChange={(event) => { handleCity(event.target.value); setOpen(true); }}
          onFocus={() => { if (display.trim().length >= PLACE_MIN_QUERY) setOpen(true); }}
          onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}
          placeholder={country.trim() ? words.cityPlaceholder : t(language, "place.countryFirst")}
          role="combobox"
          aria-expanded={open}
          aria-controls={cityListId}
          aria-autocomplete="list"
          autoComplete="off"
          required
        />
        {/* I1: a REAL dropdown, so the member sees the mechanism the moment they type. It shows
            "Searching..." during the debounce and the request, then the suggestions. Mousedown
            (not click) commits the pick, because blur would otherwise close the list first. */}
        {open && (searching || cityResults.length > 0 || display.trim().length >= PLACE_MIN_QUERY) ? (
          <ul className="birthplace-field__menu" id={cityListId} role="listbox">
            {searching ? (
              <li className="birthplace-field__note" role="presentation">{t(language, "place.searching")}</li>
            ) : lookup === "unavailable" ? (
              // R97: ABOVE the results arm and above the no-match arm, deliberately and by POSITION
              // (R52), because the shape of this defect is a failure wearing the sentence of a
              // successful empty answer. A lookup that did not answer has no rows to show and is
              // not evidence that her town does not exist.
              <li className="birthplace-field__note" role="presentation">{t(language, "place.lookupFailed")}</li>
            ) : cityResults.length ? (
              cityResults.map((result) => {
                const label = birthplaceResultLabel(result);
                return (
                  <li key={`${result.place_name}-${result.latitude}-${result.longitude}`}>
                    <button
                      type="button"
                      className="birthplace-field__item"
                      role="option"
                      aria-selected={display === label}
                      onMouseDown={(event) => { event.preventDefault(); handleCity(label); setOpen(false); }}
                    >
                      {label}
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="birthplace-field__note" role="presentation">{t(language, "place.noMatches")}</li>
            )}
          </ul>
        ) : null}
        {pickNote ? (
          <span className={pickNote.className}>
            {t(language, pickNote.key)}
          </span>
        ) : null}
      </label>
    </>
  );
}

// R75 — A SAVED PERSON, TURNED BACK INTO THE FORM EVERY READING ON THIS SITE IS BUILT FROM.
//
// It lives HERE rather than in App.tsx because this is where the blank it patches (initialBirthChart)
// and the three field blocks that emit these exact keys already are — and because a gate CALLS this
// file. R12 paid for that argument once already: on signup, where she was BORN and where she LIVES
// NOW arrive as the same keys, and **one key in the wrong column renders a perfectly good reading
// about the wrong person, with nothing on screen looking wrong.**
/**
 * >>> *I DO NOT KNOW THE TIME* — ONE CONTROL, EVERY FORM THAT ASKS FOR A BIRTH TIME. <<<
 *
 * HER IT SHEET, #126 (`snfergus@yahoo.com`): *"there is no option for unknown birth time when adding
 * a person to my room."* Measured: that form READ `birthTimeUnknown` to disable the time fields and
 * **nothing anywhere set it** — the flag was permanently false, so a member adding somebody whose
 * birth time she does not know had no way to say so. R38/R165's trap: a capability the form has,
 * reads, and cannot be given.
 *
 * It is ONE component rather than the four lines pasted a second time, because a second copy is how
 * the next form comes to be missing it (R28/R29) — and the label is one catalog row, so the two
 * forms cannot say it differently in either language (ITEM 14b).
 *
 * Hookless, so a gate CALLS it and reads the tree back (R7/R8).
 */
export function UnknownTimeCheck({ checked, onChange, label }: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label className="unknown-time-check">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

export function personToChart(p: { id?: string; name?: string; firstName?: string; middleName?: string; lastName?: string; birthDate?: string; birthTime?: string; birthPeriod?: string; birthTimeUnknown?: boolean; birthplace?: string; birthRegion?: string; birthCountry?: string; latitude?: string; longitude?: string; timezone?: string }): BirthChartForm {
  // Q2 fix: carry the name PARTS (first/middle/last), not just the combined name, so selecting a
  // person fills every field — numerology needs the middle name or it produces wrong numbers.
  return withNameParts({
    ...initialBirthChart,
    // I6 R1: carry WHICH ROW this is, so the reading is keyed to the row the member picked.
    subjectKind: "person",
    subjectId: p.id ? String(p.id) : undefined,
    name: p.name || "",
    firstName: p.firstName || "",
    middleName: p.middleName || "",
    lastName: p.lastName || "",
    birthDate: p.birthDate || "",
    birthTime: p.birthTime || "",
    birthPeriod: p.birthPeriod === "PM" ? "PM" : "AM",
    birthTimeUnknown: Boolean(p.birthTimeUnknown),
    birthplace: p.birthplace || "",
    birthRegion: p.birthRegion || "",
    birthCountry: p.birthCountry || "",
    latitude: p.latitude || "",
    longitude: p.longitude || "",
    timezone: p.timezone || "",
  });
}
